import { useFields } from "../../api/useFields";
import { useRecords } from "../../api/useRecords";
import { ResponseScore } from "../../screens/psy-evaluations/psyEvaluations.types";
import { RESPONSES_SCORES_FIELDS, buildLevelColorsFromField } from "../../screens/psy-evaluations/useScoresData";
import { resolveTrailingColumns } from "../../screens/psy-evaluations/components/scores-table/resolveTrailingColumns";
import { ScoresColumn } from "../../screens/psy-evaluations/components/scores-table/scoresTable.types";

export type InstrumentReportInstrument = {
	instrumentResponseId: number;
	instrumentName: string;
	completedAt: string | null;
	scores: Array<ResponseScore>;
	trailingColumns: Array<ScoresColumn>;
};

type FlatScore = ResponseScore & {
	instrument_response_id: { id: number; completed_at: string | null; instrument_id: { id: string; name: string } };
};

const instrumentsReportFields = [
	...RESPONSES_SCORES_FIELDS,
	"instrument_response_id.id",
	"instrument_response_id.completed_at",
	"instrument_response_id.instrument_id.id",
	"instrument_response_id.instrument_id.name",
].join(",");

export const useInstrumentsReportData = (instrumentResponseIds: Array<string>, enabled: boolean) => {
	const { formatedFields, status: fieldsStatus } = useFields("responses_scores", {
		fieldsToRemove: { fieldNamesToRemove: ["instrument_response_id"] },
	});
	const { formatedFields: cutoffFields, status: cutoffFieldsStatus } = useFields("instrument_dimension_cutoffs");
	const levelField = cutoffFields.find((field) => field.field === "level");
	const levelColors = buildLevelColorsFromField(levelField);

	const hasSelection = instrumentResponseIds.length > 0;
	const { data, status } = useRecords("responses_scores", instrumentsReportFields, {
		enabled: enabled && hasSelection,
		extraOptions: {
			filter: { instrument_response_id: { id: { _in: instrumentResponseIds } } },
			sort: "instrument_dimension.sort",
		},
	});

	const rows = (data?.items ?? []) as Array<FlatScore>;
	const instruments = groupByInstrument(rows);
	const isLoading = (hasSelection && status === "loading") || fieldsStatus === "loading" || cutoffFieldsStatus === "loading";
	const isError = status === "error" || fieldsStatus === "error" || cutoffFieldsStatus === "error";

	return { instruments, formatedFields, levelColors, isLoading, isError };
};

const groupByInstrument = (rows: Array<FlatScore>) => {
	const instrumentsById = new Map<number, InstrumentReportInstrument>();
	for (const row of rows) {
		const instrumentResponse = row.instrument_response_id;
		if (!instrumentResponse) continue;
		let instrument = instrumentsById.get(instrumentResponse.id);
		if (!instrument) {
			instrument = {
				instrumentResponseId: instrumentResponse.id,
				instrumentName: instrumentResponse.instrument_id?.name ?? "",
				completedAt: instrumentResponse.completed_at ?? null,
				scores: [],
				trailingColumns: resolveTrailingColumns(instrumentResponse.instrument_id?.id),
			};
			instrumentsById.set(instrumentResponse.id, instrument);
		}
		instrument.scores.push(row);
	}
	const instruments = Array.from(instrumentsById.values());
	instruments.sort((first, second) => (first.completedAt ?? "").localeCompare(second.completedAt ?? ""));
	return instruments;
};
